import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Autocomplete,
  TablePagination,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ListFormManagement.css';
import { LoaderWrapper } from '../../components/index';
import { 
  getListFormList, 
  addListForm, 
  updateListForm, 
  deleteListForm,
  getStandardsByField,
  getCategoriesByFieldAndStandard,
  getDisplayIdsByCategory 
} from '../../services/api';
import FileUploadComponent from '../../components/FileUploadComponent';


// Utility functions for field types (similar to other management components)
const getAllowedFieldTypes = (allFieldTypes) => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  const storedLabTypeId = localStorage.getItem('labTypeId');
  
  let allowedFieldTypes = [];
  
  if (storedLabTypeIds) {
    const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    // Map lab type IDs to field types
    allowedFieldTypes = allFieldTypes.filter(field => {
      if (allowedIds.includes(1) && field.value === 'Testing') return true;
      if (allowedIds.includes(3) && field.value === 'Medical') return true;
      if (allowedIds.includes(2) && field.value === 'Calibration') return true;
      if (allowedIds.includes(4) && field.value === 'PTP') return true;
      if (allowedIds.includes(5) && field.value === 'RMP') return true;
      return false;
    });
  } else if (storedLabTypeId) {
    const labTypeId = parseInt(storedLabTypeId);
    // Map single lab type ID to corresponding field type
    if (labTypeId === 1) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'Testing');
    else if (labTypeId === 3) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'Medical');
    else if (labTypeId === 2) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'Calibration');
    else if (labTypeId === 4) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'PTP');
    else if (labTypeId === 5) allowedFieldTypes = allFieldTypes.filter(f => f.value === 'RMP');
  }
  
  // If no specific mapping found or no localStorage data, return all field types except the placeholder
  if (allowedFieldTypes.length === 0) {
    allowedFieldTypes = allFieldTypes.filter(field => field.value !== '0');
  }
  
  return allowedFieldTypes;
};

const shouldDisableFieldDropdown = (allowedFieldTypes) => {
  return allowedFieldTypes.length === 1;
};

// Helper function to get default field type from localStorage
const getDefaultFieldType = () => {
  const storedLabTypeIds = localStorage.getItem('labTypeIds');
  const storedLabTypeId = localStorage.getItem('labTypeId');
  
  let defaultFieldType = '';
  
  if (storedLabTypeIds) {
    const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    if (allowedIds.length > 0) {
      const firstLabTypeId = allowedIds[0];
      if (firstLabTypeId === 1) defaultFieldType = 'Testing';
      else if (firstLabTypeId === 3) defaultFieldType = 'Medical';
      else if (firstLabTypeId === 2) defaultFieldType = 'Calibration';
      else if (firstLabTypeId === 4) defaultFieldType = 'PTP';
      else if (firstLabTypeId === 5) defaultFieldType = 'RMP';
    }
  } else if (storedLabTypeId) {
    const labTypeId = parseInt(storedLabTypeId);
    if (labTypeId === 1) defaultFieldType = 'Testing';
    else if (labTypeId === 3) defaultFieldType = 'Medical';
    else if (labTypeId === 2) defaultFieldType = 'Calibration';
    else if (labTypeId === 4) defaultFieldType = 'PTP';
    else if (labTypeId === 5) defaultFieldType = 'RMP';
  }
  
  return defaultFieldType;
};

const ListFormManagement = () => {
  const [loading, setLoading] = useState(false);
  const [listFormList, setListFormList] = useState([]);
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [displayIds, setDisplayIds] = useState([]);

  // Quill editor configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ];
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filter state
  const [filters, setFilters] = useState({
    field: '',
    standard: '',
    category: '',
    displayId: '',
    name: '',
  });
  
  // Form state
  const [formData, setFormData] = useState({
    field: '0',
    standard: '',
    categoryId: '',
    displayId: '',
    name: '',
    file: null,
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  const fieldTypes = [
    { value: '0', label: 'Select Field' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Calibration', label: 'Calibration' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
  ];

  // Get filtered field types using utility (similar to other management components)
  const filteredFieldTypes = getAllowedFieldTypes(fieldTypes);

  useEffect(() => {
    fetchListFormList();
  }, []);

  // Set initial field type from localStorage (similar to other management components)
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultFieldType = '0';
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        // Map first lab type ID to corresponding field type
        const firstLabTypeId = allowedIds[0];
        if (firstLabTypeId === 1) defaultFieldType = 'Testing';
        else if (firstLabTypeId === 3) defaultFieldType = 'Medical';
        else if (firstLabTypeId === 2) defaultFieldType = 'Calibration';
        else if (firstLabTypeId === 4) defaultFieldType = 'PTP';
        else if (firstLabTypeId === 5) defaultFieldType = 'RMP';
      }
    } else if (storedLabTypeId) {
      const labTypeId = parseInt(storedLabTypeId);
      if (labTypeId === 1) defaultFieldType = 'Testing';
      else if (labTypeId === 3) defaultFieldType = 'Medical';
      else if (labTypeId === 2) defaultFieldType = 'Calibration';
      else if (labTypeId === 4) defaultFieldType = 'PTP';
      else if (labTypeId === 5) defaultFieldType = 'RMP';
    }
    
    // Only set if we have a valid field type and it's different from current
    if (defaultFieldType !== '0' && defaultFieldType !== formData.field) {
      setFormData(prev => ({
        ...prev,
        field: defaultFieldType
      }));
    }
  }, []);

  // Initialize filter field type from localStorage
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultFilterFieldType = '';
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        const firstLabTypeId = allowedIds[0];
        if (firstLabTypeId === 1) defaultFilterFieldType = 'Testing';
        else if (firstLabTypeId === 3) defaultFilterFieldType = 'Medical';
        else if (firstLabTypeId === 2) defaultFilterFieldType = 'Calibration';
        else if (firstLabTypeId === 4) defaultFilterFieldType = 'PTP';
        else if (firstLabTypeId === 5) defaultFilterFieldType = 'RMP';
      }
    } else if (storedLabTypeId) {
      const labTypeId = parseInt(storedLabTypeId);
      if (labTypeId === 1) defaultFilterFieldType = 'Testing';
      else if (labTypeId === 3) defaultFilterFieldType = 'Medical';
      else if (labTypeId === 2) defaultFilterFieldType = 'Calibration';
      else if (labTypeId === 4) defaultFilterFieldType = 'PTP';
      else if (labTypeId === 5) defaultFilterFieldType = 'RMP';
    }
    
    // Set filter field type if we have a default and it's not already set
    if (defaultFilterFieldType && !filters.field) {
      setFilters(prev => ({
        ...prev,
        field: defaultFilterFieldType
      }));
    }
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Fetch standards when field changes
  useEffect(() => {
    if (formData.field && formData.field !== '0') {
      fetchStandards(formData.field);
    } else {
      setStandards([]);
      setFormData(prev => ({ ...prev, standard: '', categoryId: '', displayId: '' }));
      setCategories([]);
      setDisplayIds([]);
    }
  }, [formData.field]);

  // Fetch categories when standard changes
  useEffect(() => {
    if (formData.standard && formData.field && formData.field !== '0') {
      fetchCategories(formData.field, formData.standard);
    } else {
      setCategories([]);
      setFormData(prev => ({ ...prev, categoryId: '', displayId: '' }));
      setDisplayIds([]);
    }
  }, [formData.standard]);

  // Fetch display IDs when category changes
  useEffect(() => {
    if (formData.categoryId) {
      fetchDisplayIds(formData.categoryId);
    } else {
      setDisplayIds([]);
      setFormData(prev => ({ ...prev, displayId: '' }));
    }
  }, [formData.categoryId]);

  const fetchListFormList = async () => {
    try {
      setLoading(true);
      const response = await getListFormList();
      if (response.data.success) {
        setListFormList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching list/form data:', error);
      showSnackbar('Error fetching list/form data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStandards = async (field) => {
    try {
      const response = await getStandardsByField(field);
      if (response.data.success) {
        setStandards(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching standards:', error);
      showSnackbar('Error fetching standards', 'error');
    }
  };

  const fetchCategories = async (field, standard) => {
    try {
      const response = await getCategoriesByFieldAndStandard(field, standard);
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Error fetching categories', 'error');
    }
  };

  const fetchDisplayIds = async (categoryId) => {
    try {
      const response = await getDisplayIdsByCategory(categoryId);
      if (response.data.success) {
        setDisplayIds(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching display IDs:', error);
      showSnackbar('Error fetching display IDs', 'error');
    }
  };

  // Memoized filtered list
  const filteredListFormList = useMemo(() => {
    let filtered = listFormList;

    if (filters.field) {
      filtered = filtered.filter(item => item.Field === filters.field);
    }

    if (filters.standard) {
      filtered = filtered.filter(item => item.Standard === filters.standard);
    }

    if (filters.category) {
      filtered = filtered.filter(item => item.CatName === filters.category);
    }

    if (filters.displayId) {
      filtered = filtered.filter(item => item.clauseno === filters.displayId);
    }

    if (filters.name) {
      filtered = filtered.filter(item => 
        item.Name && item.Name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    return filtered;
  }, [listFormList, filters]);

  // Paginated list
  const paginatedListFormList = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredListFormList.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredListFormList, page, rowsPerPage]);

  const clearFilters = useCallback(() => {
    setFilters({
      field: getDefaultFieldType(),
      standard: '',
      category: '',
      displayId: '',
      name: '',
    });
    setPage(0);
  }, []);

  // Get unique values for filter dropdowns - memoized
  const uniqueFields = useMemo(() => {
    const fields = [...new Set(listFormList.map(item => item.Field).filter(Boolean))];
    // Filter based on allowed field types
    const allowedFieldValues = filteredFieldTypes.map(f => f.value);
    return fields.filter(field => allowedFieldValues.includes(field));
  }, [listFormList, filteredFieldTypes]);

  const uniqueStandards = useMemo(() => {
    let standards = [...new Set(listFormList.map(item => item.Standard).filter(Boolean))];
    
    if (filters.field) {
      standards = [...new Set(
        listFormList
          .filter(item => item.Field === filters.field)
          .map(item => item.Standard)
          .filter(Boolean)
      )];
    }
    
    return standards;
  }, [listFormList, filters.field]);

  const uniqueCategories = useMemo(() => {
    let categories = [...new Set(listFormList.map(item => item.CatName).filter(Boolean))];
    
    if (filters.standard) {
      categories = [...new Set(
        listFormList
          .filter(item => item.Standard === filters.standard)
          .map(item => item.CatName)
          .filter(Boolean)
      )];
    }
    
    return categories;
  }, [listFormList, filters.standard]);

  const uniqueDisplayIds = useMemo(() => {
    return [...new Set(listFormList.map(item => item.clauseno).filter(Boolean))];
  }, [listFormList]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.field || formData.field === '0') {
      newErrors.field = 'Field Type is required';
    }
    
    if (!formData.standard) {
      newErrors.standard = 'Standard is required';
    }
    
    if (!formData.displayId || formData.displayId === '') {
      newErrors.displayId = 'Display Id is required';
    }
    
    // Check if name has actual content (not just empty HTML tags)
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.name;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    if (!formData.name || textContent.trim() === '') {
      newErrors.name = 'Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  }, [errors]);

  const handleFileChange = (file) => {
    // const file = e.target.files[0];
    // if (file) {
    //   // Validate file type
    //   const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    //   if (!validTypes.includes(file.type)) {
    //     showSnackbar('Only PDF, JPG, JPEG, and PNG files are allowed', 'error');
    //     e.target.value = '';
    //     return;
    //   }
      
    //   // Validate file size (max 10MB)
    //   const maxSize = 10 * 1024 * 1024; // 10MB
    //   if (file.size > maxSize) {
    //     showSnackbar('File size must not exceed 10MB', 'error');
    //     e.target.value = '';
    //     return;
    //   }
      
      setFormData(prev => ({ ...prev, file }));
    //}
  };

  const handleSubmit = useCallback(async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  try {
    setLoading(true);

    const payload = {
      field: formData.field,
      standard: formData.standard,
      clauseid: formData.categoryId || "",
      clauseno: formData.displayId,
      name: formData.name.trim(),
      file: formData.file || null,
    };

    const response = await addListForm(payload);

    if (response.data.success) {
      showSnackbar("List/Form added successfully", "success");
      resetForm();
      fetchListFormList();
      setPage(0);
    } else {
      showSnackbar(
        response.data.message || "Error adding list/form",
        "error"
      );
    }
  } catch (error) {
    console.error("Error adding list/form:", error);

    const errorMessage =
      error.response?.data?.message ||
      "Error adding list/form";

    showSnackbar(errorMessage, "error");
  } finally {
    setLoading(false);
  }
}, [formData]);

  const handleUpdate = useCallback(async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  try {
    setLoading(true);

    const payload = {
      id: editId,
      field: formData.field,
      standard: formData.standard,
      clauseid: formData.categoryId || "",
      clauseno: formData.displayId || "",
      name: formData.name.trim(),
      file: formData.file ? (typeof formData.file === "object"? formData.file.url: formData.file || null) : null,
    };

    const response = await updateListForm(payload);

    if (response.data.success) {
      showSnackbar("List/Form updated successfully", "success");
      resetForm();
      fetchListFormList();
    } else {
      showSnackbar(
        response.data.message || "Error updating list/form",
        "error"
      );
    }
  } catch (error) {
    console.error("Error updating list/form:", error);

    const errorMessage =
      error.response?.data?.message ||
      "Error updating list/form";

    showSnackbar(errorMessage, "error");
  } finally {
    setLoading(false);
  }
}, [formData, editId]);

  const handleEdit = useCallback(async (item) => {
    console.log('Editing list/form:', item);
    
    // Convert plain text to HTML if it's not already HTML
    let nameContent = item.Name || '';
    if (nameContent && !nameContent.includes('<')) {
      // Convert plain text to HTML paragraphs
      nameContent = nameContent.split('\n').map(line => 
        line.trim() ? `<p>${line}</p>` : '<p><br></p>'
      ).join('');
    }
    
    setFormData({
      field: item.Field,
      standard: item.Standard,
      categoryId: item.clauseid || '',
      displayId: item.clauseno || '',
      name: nameContent,
      file: item.Listfile? createFileObject(item.Listfile) : null,
    });
    setEditMode(true);
    setEditId(item.id);
    
    // Fetch cascading dropdowns for edit mode
    if (item.Field && item.Field !== '0') { 
      console.log('Fetching cascading data for edit mode', item.Field);    
      await fetchStandards(item.Field);
      if (item.Standard) {
        await fetchCategories(item.Field, item.Standard);
        if (item.clauseid) {
          await fetchDisplayIds(item.clauseid);
        }
      }
    }
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName.split('/').pop(),
      url: fileName,
      key: `live/${fileName.split('/').pop()}`,
      originalName: fileName.split('/').pop(),
    };
  };


  const handleDeleteClick = useCallback((id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deleteListForm(deleteId);
      
      if (response.data.success) {
        showSnackbar('List/Form deleted successfully', 'success');
        fetchListFormList();
      } else {
        showSnackbar(response.data.message || 'Error deleting list/form', 'error');
      }
    } catch (error) {
      console.error('Error deleting list/form:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting list/form';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  }, [deleteId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  }, []);

  const resetForm = useCallback(() => {
    // Get default field type from localStorage like in form initialization
    const storedLabTypeIds = localStorage.getItem('labTypeIds');
    const storedLabTypeId = localStorage.getItem('labTypeId');
    
    let defaultFieldType = '0';
    
    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (allowedIds.length > 0) {
        const firstLabTypeId = allowedIds[0];
        if (firstLabTypeId === 1) defaultFieldType = 'Testing';
        else if (firstLabTypeId === 3) defaultFieldType = 'Medical';
        else if (firstLabTypeId === 2) defaultFieldType = 'Calibration';
        else if (firstLabTypeId === 4) defaultFieldType = 'PTP';
        else if (firstLabTypeId === 5) defaultFieldType = 'RMP';
      }
    } else if (storedLabTypeId) {
      const labTypeId = parseInt(storedLabTypeId);
      if (labTypeId === 1) defaultFieldType = 'Testing';
      else if (labTypeId === 3) defaultFieldType = 'Medical';
      else if (labTypeId === 2) defaultFieldType = 'Calibration';
      else if (labTypeId === 4) defaultFieldType = 'PTP';
      else if (labTypeId === 5) defaultFieldType = 'RMP';
    }

    setFormData({
      field: defaultFieldType,
      standard: '',
      categoryId: '',
      displayId: '',
      name: '',
      file: null,
    });
    setEditMode(false);
    setEditId(null);
    setErrors({});
    setStandards([]);
    setCategories([]);
    setDisplayIds([]);
    // Reset file input
    const fileInput = document.getElementById('file-upload-input');
    if (fileInput) fileInput.value = '';
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Pagination handlers
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleViewDocument = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{ mb: 3, fontWeight: 600, color: "#00024c" }}
      >
        {editMode ? "Edit List/Form" : "Add List/Form"}
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={editMode ? handleUpdate : handleSubmit}>
          <Grid container spacing={3}>
            {/* Field Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.field} required>
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  label="Field Type"
                  disabled={shouldDisableFieldDropdown(filteredFieldTypes)}
                >
                  <MenuItem value="0" disabled>
                    <em>Select Field</em>
                  </MenuItem>
                  {filteredFieldTypes.length > 0 ? (
                    filteredFieldTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>No Field Types Available</em>
                    </MenuItem>
                  )}
                </Select>
                {errors.field && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.field}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Standard */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!errors.standard}
                required
                disabled={!formData.field || formData.field === "0"}
              >
                <InputLabel>Standard</InputLabel>
                <Select
                  name="standard"
                  value={formData.standard}
                  onChange={handleInputChange}
                  label="Standard"
                >
                  <MenuItem value="">
                    <em>Select Standard</em>
                  </MenuItem>
                  {standards.map((std) => (
                    <MenuItem
                      key={std.Id || std.id}
                      value={std.standard || std.Standard}
                    >
                      {std.standard || std.Standard}
                    </MenuItem>
                  ))}
                </Select>
                {errors.standard && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.standard}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Category Name */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!formData.standard}>
                <InputLabel>Category Name</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleInputChange}
                  label="Category Name"
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.Id || cat.id} value={cat.Id || cat.id}>
                      {cat.CatName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Display Id */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={!!errors.displayId}
                required
                disabled={!formData.categoryId}
              >
                <InputLabel>Display Id</InputLabel>
                <Select
                  name="displayId"
                  value={formData.displayId}
                  onChange={handleInputChange}
                  label="Display Id"
                >
                  <MenuItem value="">
                    <em>Select Display Id</em>
                  </MenuItem>
                  {displayIds.map((displayId) => (
                    <MenuItem
                      key={displayId.Id || displayId.id}
                      value={displayId.DisplayName}
                    >
                      {displayId.DisplayName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.displayId && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.displayId}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Name */}
            <Grid item xs={12}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    color: errors.name ? "#d32f2f" : "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  Name <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <Box
                  sx={{
                    border: errors.name
                      ? "1px solid #d32f2f"
                      : "1px solid #c4c4c4",
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: errors.name ? "#d32f2f" : "#000",
                    },
                    "& .quill": {
                      backgroundColor: "#fff",
                    },
                    "& .ql-container": {
                      minHeight: "120px",
                      fontSize: "16px",
                    },
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={formData.name}
                    onChange={(content) => {
                      setFormData((prev) => ({ ...prev, name: content }));
                      if (errors.name) {
                        setErrors((prev) => ({ ...prev, name: "" }));
                      }
                    }}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter name with formatting..."
                  />
                </Box>
                {errors.name && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5, display: "block" }}
                  >
                    {errors.name}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <FileUploadComponent
                fieldName="file-upload-input"
                label="Upload Document"
                orgId={localStorage.getItem("orgId")}
                section="upload-documents"
                required={false}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                multiple={false}
                onChange={(fieldName, fileData) => {
                  handleFileChange(fileData ? fileData.url : null);
                }}
                initialFile={formData.file}
              />
              {/* <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload Document (PDF, JPG, JPEG, PNG)
                </Typography>
                <input
                  id="file-upload-input"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="file-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Choose File
                  </Button>
                </label>
                {formData.file && (
                  <Chip
                    label={formData.file.name}
                    onDelete={() => {
                      setFormData(prev => ({ ...prev, file: null }));
                      const fileInput = document.getElementById('file-upload-input');
                      if (fileInput) fileInput.value = '';
                    }}
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Maximum file size: 10MB. Supported formats: PDF, JPG, JPEG, PNG
                </Typography>
              </Box> */}
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={editMode ? "error" : "primary"}
                  disabled={loading}
                >
                  {editMode ? "Update" : "Submit"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ width: "100%" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#e0ebeb",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            List/Form Management
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchListFormList}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Section */}
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f9f9f9",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <FilterListIcon sx={{ mr: 1, color: "#666" }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#666" }}
                >
                  Filter List/Forms
                </Typography>
              </Box>
            </Grid>

            {/* Field Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueFields}
                value={filters.field || null}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    field: newValue || "",
                    standard: "",
                    category: "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Field" placeholder="Search" />
                )}
                clearOnEscape
                disabled={shouldDisableFieldDropdown(filteredFieldTypes)}
              />
            </Grid>

            {/* Standard Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueStandards}
                value={filters.standard || null}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    standard: newValue || "",
                    category: "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Standard"
                    placeholder="Search"
                  />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueCategories}
                value={filters.category || null}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({ ...prev, category: newValue || "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    placeholder="Search"
                  />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Display Id Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Autocomplete
                size="small"
                options={uniqueDisplayIds}
                value={filters.displayId || null}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({
                    ...prev,
                    displayId: newValue || "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Display Id"
                    placeholder="Search"
                  />
                )}
                clearOnEscape
              />
            </Grid>

            {/* Name Filter */}
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                size="small"
                label="Search Name"
                placeholder="Type to search..."
                value={filters.name || ""}
                onChange={(event) => {
                  setFilters((prev) => ({
                    ...prev,
                    name: event.target.value || "",
                  }));
                }}
                fullWidth
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                color="secondary"
              >
                Clear All Filters
              </Button>
              <Typography variant="caption" sx={{ ml: 2, color: "#666" }}>
                Showing {filteredListFormList.length} of {listFormList.length}{" "}
                items
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 100 }}
                >
                  Actions
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 120 }}
                >
                  Field
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 120 }}
                >
                  Standard
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 150 }}
                >
                  Category Name
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 120 }}
                >
                  Display Id
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 200 }}
                >
                  Name
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bold", minWidth: 120 }}
                >
                  Document
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredListFormList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {listFormList.length === 0
                        ? "No list/forms found"
                        : "No items match the selected filters"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedListFormList.map((item, index) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(item)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(item.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.Field || "N/A"}
                        variant="outlined"
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.Standard || "N/A"}
                        variant="outlined"
                        size="small"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {item.CatName || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {item.clauseno || "N/A"}
                    </TableCell>
                    <TableCell align="left">
                      <Box
                        className="listform-html-content"
                        dangerouslySetInnerHTML={{ __html: item.Name || "N/A" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {item.Listfile ? (
                        <Tooltip title="View Document">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => handleViewDocument(item.Listfile)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No file
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredListFormList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Items per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this list/form?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListFormManagement;
